/**
 * MSAL Configuration and Singleton Pattern
 *
 * This file implements a singleton pattern for MSAL (Microsoft Authentication Library)
 * to prevent multiple instances from being created, which causes warnings in the console.
 *
 * Key Features:
 * - Singleton pattern ensures only one MSAL instance exists
 * - Handles React StrictMode double-invocation gracefully
 * - Provides error handling and validation
 * - Includes debug logging to verify singleton behavior
 * - Checks for existing instances in browser memory
 *
 * Usage:
 * - Import getMsalInstance() to get the singleton instance
 * - Use resetMsalInstance() for testing or error recovery
 *
 * Why Singleton?
 * - React StrictMode double-invokes effects in development
 * - Multiple MSAL instances cause console warnings
 * - Singleton prevents this while maintaining StrictMode benefits
 */

import {
  LogLevel,
  Configuration,
  PopupRequest,
  PublicClientApplication,
} from '@azure/msal-browser';

// Extend Window interface to include MSAL instance
declare global {
  interface Window {
    msalInstance?: PublicClientApplication;
  }
}

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MEETING_ASSISTANT_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MEETING_ASSISTANT_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_MEETING_ASSISTANT_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri:
      import.meta.env.VITE_MEETING_ASSISTANT_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel:
        import.meta.env.VITE_MSAL_LOG_LEVEL === 'verbose' ? LogLevel.Verbose : LogLevel.Warning,
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
        }
      },
    },
  },
};

// Singleton pattern to ensure only one MSAL instance exists
let msalInstance: PublicClientApplication | null = null;

const checkExistingInstance = (): PublicClientApplication | null => {
  try {
    if (typeof window !== 'undefined' && window.msalInstance) {
      return window.msalInstance;
    }

    const existingInstance = sessionStorage.getItem('msal.instance');
    if (existingInstance) {
      sessionStorage.removeItem('msal.instance');
    }

    return null;
  } catch (error) {
    console.warn('Error checking for existing MSAL instance:', error);
    return null;
  }
};

export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    try {
      const existingInstance = checkExistingInstance();
      if (existingInstance) {
        msalInstance = existingInstance;
        return msalInstance;
      }

      if (!msalConfig.auth.clientId) {
        // This is mainly to prevent E-2-E tests failing from not having meeting assistant variables injected
        // The Meeting Assistant feature requires VITE_MEETING_ASSISTANT_CLIENT_ID,
        // but the rest of the app (including the primary B2C auth flow) must still
        // boot without it. MSAL defers real validation until the first auth call,
        // so creating the instance here with an empty clientId mirrors that
        // behaviour and fails loudly only when a Meeting Assistant feature is used.
        console.warn(
          'MSAL client ID is not configured. Meeting Assistant features will be unavailable.',
        );
      }

      msalInstance = new PublicClientApplication(msalConfig);

      if (typeof window !== 'undefined') {
        window.msalInstance = msalInstance;
      }

      msalInstance.initialize().catch((error) => {
        console.error('Failed to initialize MSAL instance:', error);
        msalInstance = null;
        if (typeof window !== 'undefined') {
          delete window.msalInstance;
        }
        throw error;
      });
    } catch (error) {
      console.error('Failed to create MSAL instance:', error);
      throw error;
    }
  }
  return msalInstance;
};

export const resetMsalInstance = (): void => {
  if (msalInstance) {
    msalInstance = null;
    if (typeof window !== 'undefined') {
      delete window.msalInstance;
    }
    sessionStorage.removeItem('msal.instance');
    sessionStorage.removeItem('msal.error');
    sessionStorage.removeItem('msal.nonce.idtoken');
    sessionStorage.removeItem('msal.state.login');
  }
};

export const cleanupExistingMsalState = (): void => {
  try {
    if (typeof window !== 'undefined' && window.msalInstance) {
      delete window.msalInstance;
    }

    const msalKeys = [
      'msal.instance',
      'msal.error',
      'msal.nonce.idtoken',
      'msal.state.login',
      'msal.client.info',
      'msal.session.state',
    ];

    msalKeys.forEach((key) => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error cleaning up MSAL state:', error);
  }
};

// Login request configuration with Microsoft Graph scopes
export const loginRequest: PopupRequest = {
  scopes: [
    'User.Read',
    'Calendars.Read',
    'OnlineMeetings.Read',
    'OnlineMeetingTranscript.Read.All',
    'OnlineMeetingArtifact.Read.All',
    'openid',
    'profile',
    'offline_access',
  ],
};

// Graph API configuration
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphEventsEndpoint: 'https://graph.microsoft.com/v1.0/me/events',
  graphMeetingsEndpoint: 'https://graph.microsoft.com/v1.0/me/onlineMeetings',
  graphTranscriptsEndpoint:
    'https://graph.microsoft.com/v1.0/me/onlineMeetings/{meetingId}/transcripts',
};
